import { UserAlert, UserAlertLog } from '../proto/generated/nexus.ts';
import { updateUserAlert } from '../reducer/alerts.reducer.ts';
import { createAlert, fetchUserAlerts } from '../services/apis/alerts.ts';
import { selectActiveChartId, selectIndicators } from '../selectors/chart.ts';
import {
    getAlertMessageSymbolDisplayName,
    getCreateUserAlertRequestObject,
    userAlertWithUpdatedRhsFixedPrice
} from './alerts.ts';
import store from '../store'

const CLIENT_TMP_CHART_ALERT_ID_PREFIX = 'tmp-chart-alert-'

function isClientTemporaryChartAlertId(id: string | undefined): boolean {
    return Boolean(id?.startsWith(CLIENT_TMP_CHART_ALERT_ID_PREFIX))
}

function getActiveChartForStaging() {
    const chartId = selectActiveChartId(store.getState() as any)
    return window.chartManager?.getChart(chartId)
}

class AlertsManager {
    private inFlightChartCreateRef: Set<string> = new Set()
    private recentChartCreateRef: Map<string, number> = new Map()

    updateAlertLogs(alertLog: UserAlertLog) {
        if (alertLog.alert) {
            store.dispatch(updateUserAlert(alertLog.alert))
        }
    }

    updateAlert(alert: UserAlert) {
        if (!alert) return
        if (isClientTemporaryChartAlertId(alert.id)) {
            this.stageTemporaryChartAlert(alert)
            return
        }
        store.dispatch(updateUserAlert(alert))
    }

    /** Stage a draft line on the active chart (no Redux) until the user dismisses or persists. */
    stageTemporaryChartAlert(alert: UserAlert) {
        if (!alert) return
        getActiveChartForStaging()?.stageTemporaryChartAlert(alert)
    }

    shouldUpdateAlerts(update: boolean) {
        if (update) {
            fetchUserAlerts({ dispatch: store.dispatch })
        }
    }

    /** Embedded bridge: confirm temporary chart alert and persist only on tick click. */
    async createChartTemporaryAlert(alert: UserAlert, newPrice?: number) {
        if (!alert?.id) return
        const dedupeKey = `create:${alert.id}:${newPrice ?? 'na'}`
        const now = Date.now()
        const lastSeen = this.recentChartCreateRef.get(dedupeKey) ?? 0
        if (this.inFlightChartCreateRef.has(dedupeKey) || now - lastSeen < 1200) return

        this.inFlightChartCreateRef.add(dedupeKey)
        try {
            const indicators = selectIndicators(store.getState() as any)
            const alertToCreate =
                newPrice != null
                    ? await userAlertWithUpdatedRhsFixedPrice(alert, newPrice, {
                        regenerateMessage: true,
                        indicators,
                        symbolDisplayName: getAlertMessageSymbolDisplayName(alert)
                    })
                    : alert
            const result = await createAlert(getCreateUserAlertRequestObject(alertToCreate))
            store.dispatch(updateUserAlert(result))
            getActiveChartForStaging()?.clearAllChartAlertsTemporaryDrafts()
        } finally {
            this.inFlightChartCreateRef.delete(dedupeKey)
            this.recentChartCreateRef.set(dedupeKey, Date.now())
        }
    }
}

export default AlertsManager